import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Copy, DollarSign, Network, Save, ShieldCheck, TrendingUp, Users, Wallet } from 'lucide-react';
import { API, toast } from '@/App';
import SearchBar from '@/components/shared/SearchBar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  formatAffiliateCurrency,
  formatAffiliateDateTime,
  getAffiliateStatusLabel,
  getAffiliateStatusToneClasses,
  getPayoutStatusLabel,
  getPayoutStatusVariant,
} from '@/utils/affiliateWorkspace';

const INITIAL_FORM = {
  email: '',
  full_name: '',
  phone: '',
  password: '',
  note: '',
};

const copyText = async (value) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success('Lien copie.');
  } catch (error) {
    toast.error('Impossible de copier le lien.');
  }
};

const SuperAmbassadorsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [summary, setSummary] = useState({});
  const [items, setItems] = useState([]);
  const [programSettings, setProgramSettings] = useState({});
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [formState, setFormState] = useState(INITIAL_FORM);

  const loadData = async () => {
    setLoading(true);
    try {
      const [superResponse, payoutsResponse] = await Promise.all([
        axios.get(`${API}/admin/super-ambassadors`),
        axios.get(`${API}/admin/affiliates/payouts`, { params: { affiliate_type: 'super_ambassador' } }),
      ]);
      const payload = superResponse.data || {};
      setSummary(payload.summary || {});
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setProgramSettings(payload.program_settings || {});
      setPayoutRequests(Array.isArray(payoutsResponse.data) ? payoutsResponse.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Impossible de charger les super ambassadeurs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return items;
    }
    return items.filter((item) => {
      const haystack = [
        item.user_name,
        item.user_email,
        item.affiliate_code,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      return haystack.includes(query);
    });
  }, [items, searchQuery]);

  const pendingPayouts = payoutRequests.filter((item) => String(item.status || '').toLowerCase() === 'pending').length;

  const handleToggleStatus = async (affiliateId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/affiliates/${affiliateId}`, { is_active: !currentStatus });
      toast.success(currentStatus ? 'Super ambassadeur suspendu.' : 'Super ambassadeur reactive.');
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la mise a jour.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) {
      return;
    }
    if (!String(formState.email || '').trim()) {
      toast.error("L'email est obligatoire.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/super-ambassadors`, {
        email: formState.email,
        full_name: formState.full_name || undefined,
        phone: formState.phone || undefined,
        password: formState.password || undefined,
        note: formState.note || undefined,
      });
      toast.success('Super ambassadeur prepare avec succes.');
      setFormState(INITIAL_FORM);
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossible de creer ou convertir ce super ambassadeur.");
    } finally {
      setSaving(false);
    }
  };

  const handleProcessPayout = async (payoutId, action) => {
    try {
      await axios.patch(`${API}/admin/affiliates/payouts/${payoutId}`, { status: action });
      toast.success(
        action === 'approved'
          ? 'Paiement approuve.'
          : action === 'paid'
            ? 'Paiement marque comme paye.'
            : 'Paiement rejete.'
      );
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors du traitement du paiement.');
    }
  };

  if (loading) {
    return <div className="py-8 text-sm text-slate-500">Chargement des super ambassadeurs...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Super ambassadeurs</h2>
          <p className="mt-1 text-sm text-slate-500">
            Espace admin separe pour les super ambassadeurs, leur reseau, leurs commissions directes et indirectes.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs text-slate-500">
          <Badge variant="outline">Direct vendeur: 50%</Badge>
          <Badge variant="outline">Indirect reseau: 25%</Badge>
          <Badge variant="outline">Minimum retrait: {formatAffiliateCurrency(programSettings.min_payout_amount || 0)}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <Card><CardContent className="pt-6 text-center"><Users className="mx-auto mb-2 h-8 w-8 text-violet-600" /><p className="text-2xl font-bold">{Number(summary.total || 0)}</p><p className="text-sm text-slate-500">Super ambassadeurs</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><ShieldCheck className="mx-auto mb-2 h-8 w-8 text-emerald-600" /><p className="text-2xl font-bold">{Number(summary.active || 0)}</p><p className="text-sm text-slate-500">Actifs</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><Network className="mx-auto mb-2 h-8 w-8 text-cyan-600" /><p className="text-2xl font-bold">{Number(summary.direct_ambassadors || 0)}</p><p className="text-sm text-slate-500">Ambassadeurs recrutes</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><TrendingUp className="mx-auto mb-2 h-8 w-8 text-amber-600" /><p className="text-2xl font-bold">{Number(summary.first_subscriptions_generated || 0)}</p><p className="text-sm text-slate-500">Premiers abonnements</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><DollarSign className="mx-auto mb-2 h-8 w-8 text-sky-600" /><p className="text-2xl font-bold">{formatAffiliateCurrency(summary.total_commissions || 0)}</p><p className="text-sm text-slate-500">Commissions totales</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>Creer ou promouvoir un super ambassadeur</CardTitle>
            <CardDescription>
              Creation reservee a l admin. Un compte ambassadeur existant peut etre converti via son email.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleSubmit}>
              <div>
                <Label htmlFor="super-ambassador-email">Email</Label>
                <Input
                  id="super-ambassador-email"
                  value={formState.email}
                  onChange={(event) => setFormState((current) => ({ ...current, email: event.target.value }))}
                  placeholder="super@example.com"
                />
              </div>
              <div>
                <Label htmlFor="super-ambassador-full-name">Nom complet</Label>
                <Input
                  id="super-ambassador-full-name"
                  value={formState.full_name}
                  onChange={(event) => setFormState((current) => ({ ...current, full_name: event.target.value }))}
                  placeholder="Nom complet"
                />
              </div>
              <div>
                <Label htmlFor="super-ambassador-phone">Telephone</Label>
                <Input
                  id="super-ambassador-phone"
                  value={formState.phone}
                  onChange={(event) => setFormState((current) => ({ ...current, phone: event.target.value }))}
                  placeholder="+242060000000"
                />
              </div>
              <div>
                <Label htmlFor="super-ambassador-password">Mot de passe initial</Label>
                <Input
                  id="super-ambassador-password"
                  type="password"
                  value={formState.password}
                  onChange={(event) => setFormState((current) => ({ ...current, password: event.target.value }))}
                  placeholder="Requis pour un nouveau compte"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="super-ambassador-note">Note admin</Label>
                <Input
                  id="super-ambassador-note"
                  value={formState.note}
                  onChange={(event) => setFormState((current) => ({ ...current, note: event.target.value }))}
                  placeholder="Contexte de creation ou de promotion"
                />
              </div>
              <div className="md:col-span-2">
                <Button type="submit" disabled={saving}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Enregistrement...' : 'Creer / promouvoir'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Lecture operationnelle</CardTitle>
            <CardDescription>Resume clair des volumes reseau deja traces.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="font-medium text-slate-900">Vendeurs directs</p>
              <p className="mt-1">{Number(summary.direct_vendors || 0).toLocaleString('fr-FR')}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="font-medium text-slate-900">Vendeurs indirects</p>
              <p className="mt-1">{Number(summary.indirect_vendors || 0).toLocaleString('fr-FR')}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="font-medium text-slate-900">Commissions directes / indirectes</p>
              <p className="mt-1">
                {formatAffiliateCurrency(summary.direct_commissions || 0)} / {formatAffiliateCurrency(summary.indirect_commissions || 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="super-ambassadors" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="super-ambassadors">Super ambassadeurs ({items.length})</TabsTrigger>
          <TabsTrigger value="payouts">Paiements {pendingPayouts > 0 ? `(${pendingPayouts})` : ''}</TabsTrigger>
        </TabsList>

        <TabsContent value="super-ambassadors">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle>Reseau et performances</CardTitle>
                  <CardDescription>Les super ambassadeurs restent separes des ambassadeurs standard et des vendeurs affilies.</CardDescription>
                </div>
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Rechercher un super ambassadeur..."
                  className="max-w-xs"
                />
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Compte</TableHead>
                    <TableHead>Code / liens</TableHead>
                    <TableHead>Reseau</TableHead>
                    <TableHead>Abonnements</TableHead>
                    <TableHead>Commissions</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="font-medium">{item.user_name || '-'}</div>
                        <div className="text-xs text-slate-500">{item.user_email || '-'}</div>
                        <div className="text-xs text-slate-400">{formatAffiliateDateTime(item.last_registration_at || item.created_at)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-2">
                          <code className="rounded bg-slate-100 px-2 py-1 text-xs">{item.affiliate_code || '-'}</code>
                          <div className="flex flex-wrap gap-2">
                            {item.invitation_links?.vendor ? (
                              <Button size="sm" variant="outline" onClick={() => copyText(item.invitation_links.vendor)}>
                                <Copy className="mr-2 h-3 w-3" />
                                Lien vendeur
                              </Button>
                            ) : null}
                            {item.invitation_links?.ambassador ? (
                              <Button size="sm" variant="outline" onClick={() => copyText(item.invitation_links.ambassador)}>
                                <Copy className="mr-2 h-3 w-3" />
                                Lien ambassadeur
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>Ambassadeurs: {Number(item.direct_ambassadors || 0).toLocaleString('fr-FR')}</div>
                          <div>Actifs: {Number(item.active_ambassadors || 0).toLocaleString('fr-FR')}</div>
                          <div>Vendeurs directs: {Number(item.direct_vendors || 0).toLocaleString('fr-FR')}</div>
                          <div>Vendeurs indirects: {Number(item.indirect_vendors || 0).toLocaleString('fr-FR')}</div>
                        </div>
                      </TableCell>
                      <TableCell>{Number(item.first_subscriptions_generated || 0).toLocaleString('fr-FR')}</TableCell>
                      <TableCell>
                        <div className="font-medium">{formatAffiliateCurrency(item.total_earnings || 0)}</div>
                        <div className="text-xs text-slate-500">Directes: {formatAffiliateCurrency(item.direct_commissions || 0)}</div>
                        <div className="text-xs text-slate-500">Indirectes: {formatAffiliateCurrency(item.indirect_commissions || 0)}</div>
                        <div className="text-xs text-slate-500">Disponibles: {formatAffiliateCurrency(item.available_amount || 0)}</div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${getAffiliateStatusToneClasses(item.status_code)}`}>
                          {getAffiliateStatusLabel(item.status_code)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Switch checked={Boolean(item.is_active)} onCheckedChange={() => handleToggleStatus(item.id, item.is_active)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredItems.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-500">Aucun super ambassadeur pour les filtres actuels.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payouts">
          <Card>
            <CardHeader>
              <CardTitle>Demandes de paiement super ambassadeur</CardTitle>
              <CardDescription>Pilotage separe des retraits, sans melanger les autres programmes d affiliation.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Compte</TableHead>
                    <TableHead>Montant</TableHead>
                    <TableHead>Methode</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payoutRequests.map((payout) => (
                    <TableRow key={payout.id}>
                      <TableCell>
                        <div className="font-medium">{payout.affiliate_name || '-'}</div>
                        <div className="text-xs text-slate-500">{payout.affiliate_code || '-'}</div>
                      </TableCell>
                      <TableCell>{formatAffiliateCurrency(payout.amount, payout.currency || 'FCFA')}</TableCell>
                      <TableCell>{payout.payment_method || '-'}</TableCell>
                      <TableCell>{formatAffiliateDateTime(payout.request_date)}</TableCell>
                      <TableCell>
                        <Badge variant={getPayoutStatusVariant(payout.status)}>{getPayoutStatusLabel(payout.status)}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleProcessPayout(payout.id, 'approved')}>Approuver</Button>
                          <Button size="sm" variant="outline" onClick={() => handleProcessPayout(payout.id, 'paid')}>Payer</Button>
                          <Button size="sm" variant="destructive" onClick={() => handleProcessPayout(payout.id, 'rejected')}>Rejeter</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {payoutRequests.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-500">Aucune demande de paiement super ambassadeur.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SuperAmbassadorsManagement;
